// BRIEF 87 §8 — THE CASE STAGE LINK, one implementation, both modals.
//
// "In the stages section you have several written-statement dates and hearings:
// so when you file a hearing event or a CCL task, you know which stage it is."
//
// Full width, between Type and Title, in the Event modal and the Task modal.
// ONE component and not two: the two modals are separate files by §0's ruling
// about the two VIEWS, which is a statement about CaseView and
// CollectiveCaseView — it has never been a licence to write a new field twice,
// and a picker written twice is brief 59's finding waiting to happen.
//
// ── THE TWO TIERS, AND WHICH ONE THIS FILE OWNS ────────────────────────────
//
// §8a CONVENIENCE — every activity type. Choosing a stage FILLS five fields and
// they stay MODIFIABLE and unsynced. That tier is entirely here: the picker
// writes real values into the form, the user edits them freely afterwards, and
// nothing on the server knows the difference.
//
// §8b SYNC — types whose taxonomy entry carries `meta.syncInTasksAndEvents`.
// Those fields are NOT modifiable and are resolved from the stage on every read
// by `server/src/lib/stageSync.js`. This file's whole job for that tier is to
// STOP autofilling (the server owns the values) and to say so on screen, because
// a field that silently refuses an edit is worse than one that explains itself.
//
// ── THE THREE STATES A LINKED ROW CAN BE IN ────────────────────────────────
//
// The server sends `_stageSync` and this renders each one:
//
//   'synced'   the fields come from the stage; they are read-only and labelled
//   'linked'   a stage is named but the type does not sync — §8a, fields are the
//              row's own
//   'orphan'   [RULED §8c q2] the stage was DELETED. The link is KEPT so the row
//              still says which stage it meant, the fields become editable
//              again, and the picker offers RE-POINTING at another stage — one
//              click, not a re-entry, exactly as case documents already allow
//              (CaseView.tsx:3466/:3479).
//
// ── AND A PAST EVENT NEVER MOVES ITSELF ────────────────────────────────────
//
// [RULED §8c q4] When the stage's date has moved but this row is already in the
// past, the server sends `_stageDateDiffers` instead of rewriting the date, and
// this offers a button. Silent movement could not be undone; the explicit action
// covers the reading in which the hearing "was always on the 12th".
import { FS } from "../lib/tokens";
import { resolveTaxonomyLabel } from "../lib/taxonomyHelpers";
import MenuSelect from "./MenuSelect"; // 154B §2

type StageDoc = { stageId?: number | string | null; [k: string]: unknown };
type Stage = {
  id?: number;
  type?: string | null;
  startDate?: string | null;
  /* 151 §2 step 3 — widened to match the timeline's own Stage read-set
     (CaseStageTimeline.tsx types the field string | number | null); the body
     already String()s it, so the narrower type was the primitive not fitting
     a caller, and the primitive widens (THE FOUR RULES, 4). */
  registerNumber?: string | number | null;
  juId?: number | string | null;
  notes?: string | null;
};
export type TaxEntry = { code?: string; meta?: { syncInTasksAndEvents?: boolean } | null };
export type Jur = { id?: number | string; name?: string | null };

// The app's `t`, in the one shape this file uses: a key plus an options bag
// carrying a `fallback` and any interpolation slots.
type Translate = (key: string, opts?: Record<string, unknown>) => string;
// A matter, in the read-set this field needs and nothing more.
type Container = { id?: number | string; stages?: unknown; docs?: unknown };
// The modal form. It is genuinely open — every modal's form carries dozens of
// unrelated keys — so it is indexed rather than pretended to be closed, with
// the five keys THIS component reads named so a typo in one is a compile error.
export type StageFormShape = {
  /* 184 A.0 — BOTH spellings of the case arrive here: the event form says
     `caId`, the task form says `caseId` (125 §2 — tasks.js has no `caId`
     alias), and a task's stage field had been invisible for every CASE task
     because only the first was read. */
  caId?: number | string | null;
  caseId?: number | string | null;
  ccId?: number | string | null;
  type?: string | null;
  stageId?: number | string | null;
  _stageSync?: string | null;
  _stageDateDiffers?: string | null;
  [k: string]: unknown;
};

export function stageLabel(st: Stage, t: Translate) {
  // The stage's own words, in the order a lawyer reads them: what it is, when,
  // and the register number that tells two hearings apart.
  const bits = [st.type, st.startDate ? String(st.startDate).slice(0, 10) : null, st.registerNumber]
    .filter((v) => v != null && String(v).trim() !== "");
  return bits.length ? bits.join(" · ") : t("stage.link.untitled", { fallback: "(unnamed stage)" });
}

/** Is this activity type one of §8b's synced ones? */
export function typeSyncs(typeCode: unknown, entries?: readonly TaxEntry[]) {
  if (!typeCode) return false;
  return (entries || []).some(
    (e) => e && e.code === typeCode && e.meta && e.meta.syncInTasksAndEvents === true,
  );
}

/** §8a — the five fields a stage fills. `kind` picks the row's date field.
 * 109 §7e — the composed title says the stage type's NAME, not its code:
 * `stageTypeEntries` (the `case_stage` taxonomy) resolves it, with the raw
 * code as the fallback for an absent list or an orphaned code. NOTE these are
 * NOT the `typeEntries` the field's own prop carries — those are the
 * ACTIVITY-type entries `typeSyncs` reads (the brief's ":131 already receives
 * them" pointed at that prop; measured, it holds task/event types). */
export function stageAutofill(
  st: Stage,
  kind: "task" | "event",
  ju?: readonly Jur[],
  docs?: readonly StageDoc[],
  stageTypeEntries?: readonly TaxEntry[],
) {
  // NOTE: the date field on a stage is `startDate`, NOT `date`. §8's own list
  // says "type · date · by · registerNumber · juId · notes" and `date` does not
  // exist on a stage row — measured against staging, where every element carries
  // `startDate`. Reading the brief literally fills nothing and looks like a bug.
  const jName = st.juId != null
    ? ((ju || []).find((j) => String(j.id) === String(st.juId))?.name ?? null)
    : null;
  const typeName =
    (st.type && stageTypeEntries && stageTypeEntries.length
      ? resolveTaxonomyLabel(st.type, stageTypeEntries)
      : null) || st.type;
  const desc = [typeName, st.registerNumber]
    .filter((v) => v != null && String(v).trim() !== "")
    .join(" ");
  const out: Record<string, unknown> = {};
  if (desc) out[kind === "task" ? "title" : "title"] = desc;
  if (st.startDate) out[kind === "task" ? "dueDate" : "date"] = String(st.startDate).slice(0, 10);
  if (jName) out.location = jName;
  if (st.notes) out.notes = st.notes;
  // The case documents filed under this stage — the same `stageId === st.id`
  // predicate the case documents pane uses (CaseView.tsx:3593).
  const linked = (docs || []).filter((d) => d && st.id != null && Number(d.stageId) === Number(st.id));
  if (linked.length) out._stageDocs = linked;
  return out;
}

export default function StageLinkField({
  form,
  setForm,
  cases,
  collectiveCases,
  typeEntries,
  // 109 §7e — the `case_stage` entries, for the autofilled title's NAME.
  // Distinct from `typeEntries` above, which are the ACTIVITY types.
  stageTypeEntries,
  jurisdictions,
  kind,
  t,
  label,
}: {
  form: StageFormShape;
  setForm: (fn: (p: StageFormShape) => StageFormShape) => void;
  cases?: readonly Container[];
  collectiveCases?: readonly Container[];
  typeEntries?: readonly TaxEntry[];
  stageTypeEntries?: readonly TaxEntry[];
  jurisdictions?: readonly Jur[];
  kind: "task" | "event";
  t: Translate;
  label?: string;
}) {
  const container = form.ccId
    ? (collectiveCases || []).find((c) => String(c.id) === String(form.ccId))
    : (cases || []).find((c) => String(c.id) === String(form.caId ?? form.caseId));

  // No matter chosen yet → nothing to link to. Rendering an empty select here
  // would be a control that cannot be used and cannot say why.
  if (!container) return null;
  const stages: Stage[] = Array.isArray(container.stages) ? container.stages : [];
  const synced = typeSyncs(form.type, typeEntries);
  const state = form._stageSync;
  const orphan = state === "orphan" || (form.stageId != null && !stages.some((s) => Number(s.id) === Number(form.stageId)));

  if (!stages.length && form.stageId == null) {
    return null; // the case has no stages; the field would be an empty promise
  }

  const pick = (raw: string) => {
    const id = raw ? Number(raw) : null;
    setForm((p) => {
      const next: StageFormShape = { ...p, stageId: id, _stageSync: id == null ? null : synced ? "synced" : "linked" };
      if (id == null) return next;
      const st = stages.find((s) => Number(s.id) === id);
      // §8b — a synced type does NOT autofill: the server resolves those fields
      // on every read, and writing a copy here would be the second writer the
      // §8c ruling exists to avoid.
      if (!st || synced) return next;
      const docs = Array.isArray(container.docs) ? (container.docs as StageDoc[]) : [];
      return { ...next, ...stageAutofill(st, kind, jurisdictions, docs, stageTypeEntries) };
    });
  };

  return (
    <div className="field">
      <label>
        {label || t("stage.link.label", { fallback: "Case stage link" })}
        {synced && (
          <span style={{ marginLeft: 6, fontSize: FS.s9, color: "var(--tm)" }}>
            {t("stage.link.synced_note", {
              fallback: "— kept in step with the stage",
            })}
          </span>
        )}
      </label>
      {/* 154B §2 — MenuSelect and not FieldSelect: this `.field` holds a label
          with the synced note inside it AND the orphan warning below the
          control, so the row is the site's, not the primitive's. */}
      <MenuSelect
        className="field-input"
        style={{ width: "100%" }}
        value={form.stageId != null ? String(form.stageId) : ""}
        onChange={(v) => pick(v)}
        options={[
          ["", t("stage.link.none", { fallback: "— no stage —" })] as const,
          ...stages.map(
            (st) => [String(st.id), stageLabel(st, t)] as const,
          ),
        ]}
      />
      {orphan && (
        // §8c q2 — orphan VISIBLY, and re-pointable. The select above is already
        // the re-pointing control, so this only has to say what happened.
        <div style={{ marginTop: 3, fontSize: FS.s9_5, color: "var(--am)" }}>
          {t("stage.link.deleted", {
            fallback:
              "⚠ the linked stage was deleted — these fields are editable again; pick another stage above to re-link",
          })}
        </div>
      )}
      {form._stageDateDiffers && (
        // §8c q4 — a past row never moves itself.
        <div style={{ marginTop: 3, fontSize: FS.s9_5, color: "var(--am)" }}>
          {t("stage.link.date_moved", {
            date: String(form._stageDateDiffers),
            fallback: "⚠ the stage now reads {date}. This one already happened and was left where it is.",
          })}{" "}
          <button
            type="button"
            className="btn btn-ghost"
            style={{ padding: "1px 5px", fontSize: FS.s9 }}
            onClick={() =>
              setForm((p) => ({
                ...p,
                [kind === "task" ? "dueDate" : "date"]: p._stageDateDiffers,
                _stageDateDiffers: null,
              }))
            }
          >
            {t("stage.link.apply_date", { fallback: "Apply the new date" })}
          </button>
        </div>
      )}
    </div>
  );
}
